import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { 
  Users, Plus, Search, MoreHorizontal, Edit, Trash2, Loader2,
  Upload, FileSpreadsheet, Download, CheckCircle, XCircle, AlertCircle,
  Smartphone, ShieldCheck, ShieldX, Key, KeyRound, RefreshCw, Copy, UserX,
  Calendar, Briefcase, Clock, History
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { SortableTableHead, useSortableTable } from '../../components/SortableTableHead';

// Tipos de afastamento
const TIPOS_AFASTAMENTO = [
  { value: 'ferias', label: 'Férias' },
  { value: 'licenca_paternidade', label: 'Licença Paternidade' },
  { value: 'licenca_maternidade', label: 'Licença Maternidade' },
  { value: 'licenca_casamento', label: 'Licença Casamento' },
  { value: 'licenca_luto', label: 'Licença Luto' },
  { value: 'doacao_sangue', label: 'Doação de Sangue' },
  { value: 'alistamento_eleitoral', label: 'Alistamento Eleitoral' },
  { value: 'servico_militar', label: 'Serviço Militar' },
  { value: 'outros', label: 'Outros' }
];

const EmpregadosPage = () => {
  const { api, user } = useAuth();
  const [empregados, setEmpregados] = useState([]);
  const [escalas, setEscalas] = useState([]);
  const [lideres, setLideres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [senhaDialogOpen, setSenhaDialogOpen] = useState(false);
  const [senhaTemporaria, setSenhaTemporaria] = useState('');
  const [empregadoSelecionado, setEmpregadoSelecionado] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [excluirDialogOpen, setExcluirDialogOpen] = useState(false);
  const [empregadoExcluir, setEmpregadoExcluir] = useState(null);
  const [excluindo, setExcluindo] = useState(false);
  const fileInputRef = useRef(null);
  
  // Estados para afastamentos
  const [afastamentos, setAfastamentos] = useState([]);
  const [loadingAfastamentos, setLoadingAfastamentos] = useState(false);
  const [afastamentoDialogOpen, setAfastamentoDialogOpen] = useState(false);
  const [afastamentoEdit, setAfastamentoEdit] = useState(null);
  const [afastamentoForm, setAfastamentoForm] = useState({
    tipo: '',
    tipo_outros: '',
    descricao: '',
    data_inicio: '',
    data_fim: ''
  });
  const [salvandoAfastamento, setSalvandoAfastamento] = useState(false);
  const [activeTab, setActiveTab] = useState('dados');

  // Estados para histórico de escalas
  const [historicoEscalas, setHistoricoEscalas] = useState([]);
  const [loadingHistoricoEscalas, setLoadingHistoricoEscalas] = useState(false);
  const [editHistoricoDialog, setEditHistoricoDialog] = useState(false);
  const [historicoEdit, setHistoricoEdit] = useState(null);
  const [salvandoHistorico, setSalvandoHistorico] = useState(false);
  
  // Hook para ordenação
  const { sortColumn, sortDirection, handleSort, sortData } = useSortableTable('nome', 'asc');
  
  const [formData, setFormData] = useState({
    matricula: '',
    nome: '',
    cpf: '',
    cargo: '',
    setor: '',
    data_admissao: '',
    email: '',
    telefone: '',
    escala_id: '',
    lider_id: '',
    sindicato_id: '',
    empresa_id: user?.empresa_id || '',
    nao_controla_ponto: false,
    opta_banco_horas: false
  });
  
  const [sindicatos, setSindicatos] = useState([]);

  useEffect(() => {
    fetchEmpregados();
    fetchEscalas();
    fetchLideres();
    fetchSindicatos();
  }, []);

  const fetchEmpregados = async () => {
    try {
      const response = await api.get('/empregados');
      setEmpregados(response.data);
    } catch (error) {
      console.error('Error fetching empregados:', error);
      toast.error('Erro ao carregar empregados');
    } finally {
      setLoading(false);
    }
  };

  const fetchEscalas = async () => {
    try {
      const response = await api.get('/escalas');
      setEscalas(response.data);
    } catch (error) {
      console.error('Error fetching escalas:', error);
    }
  };

  const fetchSindicatos = async () => {
    try {
      const response = await api.get('/sindicatos');
      setSindicatos(response.data.filter(s => s.ativo !== false));
    } catch (error) {
      console.error('Error fetching sindicatos:', error);
    }
  };

  const fetchLideres = async () => {
    try {
      const response = await api.get('/lideres');
      setLideres(response.data || []);
    } catch (error) {
      console.error('Error fetching lideres:', error);
    }
  };

  // Funções de Afastamentos
  const fetchAfastamentos = async (empregadoId) => {
    if (!empregadoId) return;
    setLoadingAfastamentos(true);
    try {
      const response = await api.get(`/empregados/${empregadoId}/afastamentos`);
      setAfastamentos(response.data || []);
    } catch (error) {
      console.error('Error fetching afastamentos:', error);
      setAfastamentos([]);
    } finally {
      setLoadingAfastamentos(false);
    }
  };

  const handleNovoAfastamento = () => {
    setAfastamentoEdit(null);
    setAfastamentoForm({
      tipo: '',
      tipo_outros: '',
      descricao: '',
      data_inicio: '',
      data_fim: ''
    });
    setAfastamentoDialogOpen(true);
  };

  const handleEditarAfastamento = (afastamento) => {
    setAfastamentoEdit(afastamento);
    setAfastamentoForm({
      tipo: afastamento.tipo,
      tipo_outros: afastamento.tipo_outros || '',
      descricao: afastamento.descricao || '',
      data_inicio: afastamento.data_inicio_br || '',
      data_fim: afastamento.data_fim_br || ''
    });
    setAfastamentoDialogOpen(true);
  };

  const handleSalvarAfastamento = async () => {
    if (!afastamentoForm.tipo || !afastamentoForm.data_inicio || !afastamentoForm.data_fim) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    
    if (afastamentoForm.tipo === 'outros' && !afastamentoForm.tipo_outros) {
      toast.error('Especifique o tipo de afastamento');
      return;
    }

    setSalvandoAfastamento(true);
    try {
      const payload = {
        tipo: afastamentoForm.tipo,
        tipo_outros: afastamentoForm.tipo === 'outros' ? afastamentoForm.tipo_outros : null,
        descricao: afastamentoForm.descricao || '',
        data_inicio: afastamentoForm.data_inicio,
        data_fim: afastamentoForm.data_fim
      };

      if (afastamentoEdit) {
        await api.put(`/empregados/${empregadoSelecionado.id}/afastamentos/${afastamentoEdit.id}`, payload);
        toast.success('Afastamento atualizado');
      } else {
        const response = await api.post(`/empregados/${empregadoSelecionado.id}/afastamentos`, payload);
        toast.success(`Afastamento criado. ${response.data.inconsistencias_resolvidas || 0} inconsistência(s) resolvida(s).`);
      }
      
      setAfastamentoDialogOpen(false);
      fetchAfastamentos(empregadoSelecionado.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar afastamento');
    } finally {
      setSalvandoAfastamento(false);
    }
  };

  const handleExcluirAfastamento = async (afastamentoId) => {
    if (!confirm('Deseja excluir este período de afastamento? As inconsistências serão reabertas.')) return;
    
    try {
      await api.delete(`/empregados/${empregadoSelecionado.id}/afastamentos/${afastamentoId}`);
      toast.success('Afastamento excluído');
      fetchAfastamentos(empregadoSelecionado.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir afastamento');
    }
  };

  // Funções de Histórico de Escalas
  const fetchHistoricoEscalas = async (empregadoId) => {
    if (!empregadoId) return;
    setLoadingHistoricoEscalas(true);
    try {
      const response = await api.get(`/empregados/${empregadoId}/historico-escalas`);
      setHistoricoEscalas(response.data?.historico || []);
    } catch (error) {
      console.error('Error fetching histórico escalas:', error);
      setHistoricoEscalas([]);
    } finally {
      setLoadingHistoricoEscalas(false);
    }
  };

  const formatarData = (dataStr) => {
    if (!dataStr) return '-';
    const [ano, mes, dia] = dataStr.split('-');
    return `${dia}/${mes}/${ano}`;
  };

  // Funções de edição do Histórico de Escalas
  const handleEditHistorico = (historico) => {
    setHistoricoEdit({
      ...historico,
      data_inicio: historico.data_inicio || '',
      data_fim: historico.data_fim || '',
      data_primeira_folga: historico.data_primeira_folga || '',
      dia_inicio_ciclo: historico.dia_inicio_ciclo || 1,
      observacao: historico.observacao || ''
    });
    setEditHistoricoDialog(true);
  };

  const handleSaveHistorico = async () => {
    if (!historicoEdit || !empregadoSelecionado) return;
    
    setSalvandoHistorico(true);
    try {
      await api.put(`/empregados/${empregadoSelecionado.id}/historico-escalas/${historicoEdit.id}`, {
        data_inicio: historicoEdit.data_inicio || null,
        data_fim: historicoEdit.data_fim || null,
        data_primeira_folga: historicoEdit.data_primeira_folga || null,
        dia_inicio_ciclo: parseInt(historicoEdit.dia_inicio_ciclo) || null,
        observacao: historicoEdit.observacao || null
      });
      toast.success('Histórico atualizado com sucesso');
      setEditHistoricoDialog(false);
      setHistoricoEdit(null);
      fetchHistoricoEscalas(empregadoSelecionado.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar histórico');
    } finally {
      setSalvandoHistorico(false);
    }
  };

  const handleDeleteHistorico = async (historicoId) => {
    if (!empregadoSelecionado) return;
    if (!window.confirm('Tem certeza que deseja excluir este registro do histórico?')) return;
    
    try {
      await api.delete(`/empregados/${empregadoSelecionado.id}/historico-escalas/${historicoId}`);
      toast.success('Registro excluído do histórico');
      fetchHistoricoEscalas(empregadoSelecionado.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir registro');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Padronizar CPF com zeros à esquerda (11 dígitos)
      // Converter campos vazios para null (para evitar erro de validação no backend)
      const dadosEnvio = {
        ...formData,
        cpf: formData.cpf ? formData.cpf.replace(/\D/g, '').padStart(11, '0') : null,
        email: formData.email?.trim() || null,
        telefone: formData.telefone?.trim() || null,
        escala_id: formData.escala_id || null,
        lider_id: formData.lider_id || null
      };
      
      if (empregadoSelecionado) {
        // Edição
        await api.put(`/empregados/${empregadoSelecionado.id}`, dadosEnvio);
        toast.success('Empregado atualizado com sucesso!');
      } else {
        // Novo cadastro
        await api.post('/empregados', dadosEnvio);
        toast.success('Empregado cadastrado com sucesso!');
      }
      setDialogOpen(false);
      resetForm();
      setEmpregadoSelecionado(null);
      fetchEmpregados();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar empregado');
    }
  };

  const handleEdit = (empregado) => {
    setEmpregadoSelecionado(empregado);
    setFormData({
      matricula: empregado.matricula || '',
      nome: empregado.nome || '',
      cpf: empregado.cpf || '',
      cargo: empregado.cargo || '',
      setor: empregado.setor || '',
      data_admissao: empregado.data_admissao || '',
      email: empregado.email || '',
      telefone: empregado.telefone || '',
      escala_id: empregado.escala_id || '',
      lider_id: empregado.lider_id || '',
      sindicato_id: empregado.sindicato_id || '',
      empresa_id: empregado.empresa_id || user?.empresa_id || '',
      nao_controla_ponto: empregado.nao_controla_ponto || false,
      opta_banco_horas: empregado.opta_banco_horas || false
    });
    setActiveTab('dados');
    fetchAfastamentos(empregado.id);
    fetchHistoricoEscalas(empregado.id);
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      matricula: '',
      nome: '',
      cpf: '',
      cargo: '',
      setor: '',
      data_admissao: '',
      email: '',
      telefone: '',
      escala_id: '',
      lider_id: '',
      sindicato_id: '',
      empresa_id: user?.empresa_id || '',
      nao_controla_ponto: false,
      opta_banco_horas: false
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja realmente desativar este empregado?')) return;
    try {
      await api.delete(`/empregados/${id}`);
      toast.success('Empregado desativado!');
      fetchEmpregados();
    } catch (error) {
      toast.error('Erro ao desativar empregado');
    }
  };

  const handleExcluirCompleto = async () => {
    if (!empregadoExcluir) return;
    
    setExcluindo(true);
    try {
      const response = await api.delete(`/empregados/${empregadoExcluir.id}/excluir-completo`);
      const detalhes = response.data.detalhes;
      
      toast.success(
        <div>
          <strong>{empregadoExcluir.nome}</strong> excluído permanentemente!
          <div className="text-xs mt-1 opacity-80">
            {detalhes.marcacoes} marcações, {detalhes.justificativas} justificativas removidas
          </div>
        </div>
      );
      
      setExcluirDialogOpen(false);
      setEmpregadoExcluir(null);
      fetchEmpregados();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir empregado');
    } finally {
      setExcluindo(false);
    }
  };

  const abrirDialogExclusao = (empregado) => {
    setEmpregadoExcluir(empregado);
    setExcluirDialogOpen(true);
  };

  const handleToggleAppPonto = async (empregado) => {
    const novoStatus = !empregado.app_ponto_autorizado;
    
    // Verificar se tem email e CPF cadastrados
    if (novoStatus && (!empregado.email || !empregado.cpf)) {
      toast.error('O empregado precisa ter email e CPF cadastrados para usar o app');
      return;
    }
    
    try {
      await api.put(`/empregados/${empregado.id}/app-ponto`, { autorizado: novoStatus });
      toast.success(
        novoStatus 
          ? `${empregado.nome} autorizado para usar o app de ponto` 
          : `${empregado.nome} teve acesso ao app revogado`
      );
      fetchEmpregados();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao alterar autorização');
    }
  };

  const handleResetarSenha = async (empregado, gerarTemporaria = false) => {
    try {
      const response = await api.post(`/empregados/${empregado.id}/resetar-senha`, {
        gerar_temporaria: gerarTemporaria
      });
      
      if (response.data.senha_temporaria) {
        // Mostrar modal com senha temporária
        setEmpregadoSelecionado(empregado);
        setSenhaTemporaria(response.data.senha_temporaria);
        setSenhaDialogOpen(true);
      } else {
        toast.success(`Senha de ${empregado.nome} resetada para os últimos 6 dígitos do CPF`);
      }
      fetchEmpregados();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao resetar senha');
    }
  };

  const handleForcarTrocaSenha = async (empregado) => {
    try {
      await api.put(`/empregados/${empregado.id}/forcar-troca-senha?forcar=true`);
      toast.success(`${empregado.nome} deverá trocar a senha no próximo login`);
      fetchEmpregados();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao marcar troca de senha');
    }
  };

  const copiarSenha = () => {
    navigator.clipboard.writeText(senhaTemporaria);
    toast.success('Senha copiada para a área de transferência');
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // xlsx
      'application/vnd.ms-excel', // xls
      'text/csv',
      'application/csv'
    ];
    
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (!['xlsx', 'xls', 'csv'].includes(fileExtension)) {
      toast.error('Formato inválido. Use arquivos Excel (.xlsx, .xls) ou CSV (.csv)');
      return;
    }

    setImporting(true);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(
        `/importar/empregados?empresa_id=${user?.empresa_id}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      setImportResult(response.data);
      
      if (response.data.imported > 0) {
        toast.success(`${response.data.imported} empregado(s) importado(s) com sucesso!`);
        fetchEmpregados();
      }
      
      if (response.data.errors?.length > 0) {
        toast.warning(`${response.data.errors.length} erro(s) encontrado(s)`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao importar arquivo');
      setImportResult({ imported: 0, errors: [error.response?.data?.detail || 'Erro desconhecido'] });
    } finally {
      setImporting(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const downloadTemplate = () => {
    // Create CSV template
    const headers = ['matricula', 'nome', 'cpf', 'cargo', 'setor', 'data_admissao', 'email', 'telefone'];
    const exampleRow = ['001', 'João da Silva', '123.456.789-00', 'Analista', 'TI', '2024-01-15', 'joao@email.com', '11999999999'];
    
    const csvContent = [
      headers.join(','),
      exampleRow.join(',')
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'modelo_importacao_empregados.csv';
    link.click();
    URL.revokeObjectURL(link.href);
    
    toast.success('Modelo baixado com sucesso!');
  };

  const filteredEmpregados = sortData(empregados.filter(e =>
    e.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.matricula?.includes(searchTerm) ||
    e.cpf?.includes(searchTerm)
  ));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="empregados-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-primary font-['Outfit'] mb-1">Empregados</h2>
          <p className="text-muted-foreground">Gerencie os empregados da empresa</p>
        </div>
        <div className="flex gap-3">
          {/* Import Button */}
          <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="importar-empregados-btn">
                <Upload className="w-4 h-4 mr-2" />
                Importar
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-['Outfit']">Importar Empregados</DialogTitle>
                <DialogDescription>
                  Importe empregados em massa usando arquivo Excel ou CSV
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-6">
                {/* Template Download */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <FileSpreadsheet className="w-5 h-5 text-accent mt-0.5" />
                    <div className="flex-1">
                      <p className="font-medium text-sm mb-1">Modelo de Planilha</p>
                      <p className="text-sm text-muted-foreground mb-3">
                        Baixe o modelo com as colunas obrigatórias para importação.
                      </p>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={downloadTemplate}
                        data-testid="download-modelo-btn"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Baixar Modelo CSV
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Required Columns Info */}
                <div className="space-y-2">
                  <p className="text-sm font-medium">Colunas obrigatórias:</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-accent rounded-full"></span>
                      <span>matricula</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-accent rounded-full"></span>
                      <span>nome</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-accent rounded-full"></span>
                      <span>cpf</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-accent rounded-full"></span>
                      <span>cargo</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-accent rounded-full"></span>
                      <span>setor</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-accent rounded-full"></span>
                      <span>data_admissao</span>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Colunas opcionais: email, telefone
                  </p>
                </div>

                {/* File Upload */}
                <div className="space-y-3">
                  <Label>Selecionar Arquivo</Label>
                  <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center hover:border-accent/50 transition-colors">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      onChange={handleFileSelect}
                      className="hidden"
                      id="file-upload"
                      data-testid="input-file-upload"
                    />
                    <label 
                      htmlFor="file-upload" 
                      className="cursor-pointer"
                    >
                      {importing ? (
                        <div className="flex flex-col items-center gap-2">
                          <Loader2 className="w-8 h-8 animate-spin text-accent" />
                          <span className="text-sm text-muted-foreground">Processando arquivo...</span>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-2">
                          <Upload className="w-8 h-8 text-muted-foreground" />
                          <span className="text-sm font-medium">Clique para selecionar</span>
                          <span className="text-xs text-muted-foreground">
                            Formatos aceitos: .xlsx, .xls, .csv
                          </span>
                        </div>
                      )}
                    </label>
                  </div>
                </div>

                {/* Import Result */}
                {importResult && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-4 flex-wrap">
                      <div className="flex items-center gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-accent" />
                        <span><strong>{importResult.imported}</strong> importado(s)</span>
                      </div>
                      {importResult.errors?.length > 0 && (
                        <div className="flex items-center gap-2 text-sm text-destructive">
                          <XCircle className="w-4 h-4" />
                          <span><strong>{importResult.errors.length}</strong> erro(s)</span>
                        </div>
                      )}
                      {importResult.total_linhas && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <span>de {importResult.total_linhas} linha(s) no arquivo</span>
                        </div>
                      )}
                    </div>
                    
                    {importResult.errors?.length > 0 && (
                      <div className="bg-destructive/10 rounded-lg p-4 max-h-48 overflow-y-auto border border-destructive/20">
                        <p className="text-sm font-semibold text-destructive mb-3 flex items-center gap-2">
                          <AlertCircle className="w-4 h-4" />
                          Erros encontrados no arquivo:
                        </p>
                        <ul className="text-sm space-y-2">
                          {importResult.errors.slice(0, 20).map((error, idx) => (
                            <li key={idx} className="flex items-start gap-2 bg-white/50 rounded px-2 py-1.5">
                              <span className="font-mono text-xs bg-destructive/20 text-destructive px-1.5 py-0.5 rounded">
                                {error.match(/Linha \d+/) ? error.match(/Linha \d+/)[0] : `#${idx + 1}`}
                              </span>
                              <span className="text-slate-700">
                                {error.replace(/Linha \d+: /, '')}
                              </span>
                            </li>
                          ))}
                          {importResult.errors.length > 20 && (
                            <li className="text-muted-foreground text-center py-2 border-t">
                              ... e mais {importResult.errors.length - 20} erro(s) não exibidos
                            </li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>

          {/* New Employee Button */}
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) {
              setEmpregadoSelecionado(null);
              resetForm();
            }
          }}>
            <DialogTrigger asChild>
              <Button className="bg-accent hover:bg-accent/90" data-testid="novo-empregado-btn" onClick={() => {
                setEmpregadoSelecionado(null);
                resetForm();
              }}>
                <Plus className="w-4 h-4 mr-2" />
                Novo Empregado
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-['Outfit']">
                  {empregadoSelecionado ? 'Editar Empregado' : 'Cadastrar Empregado'}
                </DialogTitle>
                <DialogDescription>
                  {empregadoSelecionado 
                    ? `Atualize os dados de ${empregadoSelecionado.nome}` 
                    : 'Preencha os dados do novo empregado'}
                </DialogDescription>
              </DialogHeader>
              
              {empregadoSelecionado ? (
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="dados" data-testid="tab-dados">
                      <Users className="w-4 h-4 mr-2" />
                      Dados
                    </TabsTrigger>
                    <TabsTrigger value="afastamentos" data-testid="tab-afastamentos">
                      <Calendar className="w-4 h-4 mr-2" />
                      Afastamentos
                    </TabsTrigger>
                    <TabsTrigger value="historico-escalas" data-testid="tab-historico-escalas">
                      <Clock className="w-4 h-4 mr-2" />
                      Histórico Escalas
                    </TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="dados" className="mt-4">
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="matricula">Matrícula *</Label>
                          <Input
                            id="matricula"
                            value={formData.matricula}
                            onChange={(e) => setFormData({...formData, matricula: e.target.value})}
                            required
                            data-testid="input-matricula"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="nome">Nome Completo *</Label>
                          <Input
                            id="nome"
                            value={formData.nome}
                            onChange={(e) => setFormData({...formData, nome: e.target.value})}
                            required
                            data-testid="input-nome"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="cpf">CPF *</Label>
                          <Input
                            id="cpf"
                            value={formData.cpf}
                            onChange={(e) => setFormData({...formData, cpf: e.target.value})}
                            placeholder="000.000.000-00"
                            required
                            data-testid="input-cpf"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="data_admissao">Data Admissão *</Label>
                          <Input
                            id="data_admissao"
                            type="date"
                            value={formData.data_admissao}
                            onChange={(e) => setFormData({...formData, data_admissao: e.target.value})}
                            required
                            data-testid="input-data-admissao"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="cargo">Cargo *</Label>
                          <Input
                            id="cargo"
                            value={formData.cargo}
                            onChange={(e) => setFormData({...formData, cargo: e.target.value})}
                            required
                            data-testid="input-cargo"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="setor">Setor *</Label>
                          <Input
                            id="setor"
                            value={formData.setor}
                            onChange={(e) => setFormData({...formData, setor: e.target.value})}
                            required
                            data-testid="input-setor"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="sindicato">Sindicato/Categoria</Label>
                          <Select
                            value={formData.sindicato_id || "none"}
                            onValueChange={(value) => setFormData({...formData, sindicato_id: value === "none" ? "" : value})}
                          >
                            <SelectTrigger data-testid="select-sindicato">
                              <SelectValue placeholder="Selecione (opcional)" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Nenhum (usa regras gerais)</SelectItem>
                              {sindicatos.map(s => (
                                <SelectItem key={s.id} value={s.id}>
                                  {s.nome} {s.sigla ? `(${s.sigla})` : ''}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email">E-mail</Label>
                          <Input
                            id="email"
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({...formData, email: e.target.value})}
                            data-testid="input-email"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="telefone">Telefone</Label>
                          <Input
                            id="telefone"
                            value={formData.telefone}
                            onChange={(e) => setFormData({...formData, telefone: e.target.value})}
                            data-testid="input-telefone"
                          />
                        </div>
                        <div className="space-y-2 flex items-end">
                          <div className="flex items-center space-x-2 bg-blue-50 p-3 rounded-lg w-full">
                            <input
                              type="checkbox"
                              id="opta_banco_horas"
                              checked={formData.opta_banco_horas || false}
                              onChange={(e) => setFormData({...formData, opta_banco_horas: e.target.checked})}
                              className="h-4 w-4 rounded border-blue-300 text-blue-600 focus:ring-blue-500"
                              data-testid="checkbox-opta-banco-horas"
                            />
                            <Label htmlFor="opta_banco_horas" className="cursor-pointer font-medium text-blue-800">
                              Opta por Banco de Horas
                            </Label>
                          </div>
                        </div>
                        <div className="col-span-2 space-y-2">
                          <Label htmlFor="escala">Escala</Label>
                          <Select
                            value={formData.escala_id}
                            onValueChange={(value) => setFormData({...formData, escala_id: value})}
                          >
                            <SelectTrigger data-testid="select-escala">
                              <SelectValue placeholder="Selecione uma escala" />
                            </SelectTrigger>
                            <SelectContent>
                              {escalas.map(escala => (
                                <SelectItem key={escala.id} value={escala.id}>
                                  {escala.nome} ({escala.tipo})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-2 space-y-2">
                          <Label htmlFor="lider">Líder Direto</Label>
                          <Select
                            value={formData.lider_id || "none"}
                            onValueChange={(value) => setFormData({...formData, lider_id: value === "none" ? "" : value})}
                          >
                            <SelectTrigger data-testid="select-lider">
                              <SelectValue placeholder="Selecione o líder direto" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Nenhum</SelectItem>
                              {lideres.map(lider => (
                                <SelectItem key={lider.id} value={lider.empregado_id || lider.usuario_id}>
                                  {lider.empregado_nome || lider.usuario_nome} - {lider.empregado_setor || 'Sem setor'}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-muted-foreground">
                            O líder será responsável por aprovar justificativas deste empregado
                          </p>
                        </div>
                        
                        {/* Checkbox Não Controla Ponto */}
                        <div className="col-span-2 flex items-center space-x-2 bg-slate-50 p-3 rounded-lg">
                          <input
                            type="checkbox"
                            id="nao_controla_ponto"
                            checked={formData.nao_controla_ponto || false}
                            onChange={(e) => setFormData({...formData, nao_controla_ponto: e.target.checked})}
                            className="h-4 w-4 rounded border-slate-300 text-accent focus:ring-accent"
                            data-testid="checkbox-nao-controla-ponto"
                          />
                          <div>
                            <Label htmlFor="nao_controla_ponto" className="cursor-pointer font-medium">
                              Não controla ponto
                            </Label>
                            <p className="text-xs text-slate-500">
                              Marque para sócios, diretores ou cargos de confiança que não precisam registrar ponto. Não serão geradas inconsistências.
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-end gap-3 pt-4">
                        <Button type="button" variant="outline" onClick={() => {
                          setDialogOpen(false);
                          setEmpregadoSelecionado(null);
                          resetForm();
                        }}>
                          Cancelar
                        </Button>
                        <Button type="submit" className="bg-accent hover:bg-accent/90" data-testid="submit-empregado-btn">
                          Atualizar
                        </Button>
                      </div>
                    </form>
                  </TabsContent>
                  
                  <TabsContent value="afastamentos" className="mt-4">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-medium">Períodos de Afastamento</h3>
                          <p className="text-sm text-muted-foreground">Férias, licenças e outros afastamentos</p>
                        </div>
                        <Button onClick={handleNovoAfastamento} size="sm" data-testid="btn-novo-afastamento">
                          <Plus className="w-4 h-4 mr-2" />
                          Novo Período
                        </Button>
                      </div>
                      
                      {loadingAfastamentos ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="w-6 h-6 animate-spin text-accent" />
                        </div>
                      ) : afastamentos.length === 0 ? (
                        <div className="text-center py-8 bg-slate-50 rounded-lg">
                          <Briefcase className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                          <p className="text-sm text-muted-foreground">Nenhum período de afastamento cadastrado</p>
                        </div>
                      ) : (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Tipo</TableHead>
                              <TableHead>Período</TableHead>
                              <TableHead>Descrição</TableHead>
                              <TableHead className="text-right">Ações</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {afastamentos.map((af) => (
                              <TableRow key={af.id}>
                                <TableCell>
                                  <Badge variant="outline">
                                    {af.tipo_label || af.tipo}
                                    {af.tipo === 'outros' && af.tipo_outros && `: ${af.tipo_outros}`}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-sm">
                                  {af.data_inicio_br} a {af.data_fim_br}
                                </TableCell>
                                <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                                  {af.descricao || '-'}
                                </TableCell>
                                <TableCell className="text-right">
                                  <div className="flex justify-end gap-2">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleEditarAfastamento(af)}
                                      data-testid={`btn-editar-afastamento-${af.id}`}
                                    >
                                      <Edit className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="text-destructive hover:text-destructive"
                                      onClick={() => handleExcluirAfastamento(af.id)}
                                      data-testid={`btn-excluir-afastamento-${af.id}`}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                      
                      <div className="flex justify-end pt-4 border-t">
                        <Button variant="outline" onClick={() => {
                          setDialogOpen(false);
                          setEmpregadoSelecionado(null);
                          resetForm();
                        }}>
                          Fechar
                        </Button>
                      </div>
                    </div>
                  </TabsContent>

                  {/* Aba Histórico de Escalas */}
                  <TabsContent value="historico-escalas" className="mt-4">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold flex items-center gap-2">
                          <History className="w-5 h-5" />
                          Histórico de Escalas
                        </h3>
                        <p className="text-sm text-slate-500">
                          Registro de todas as escalas vinculadas ao funcionário
                        </p>
                      </div>

                      {loadingHistoricoEscalas ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                          <span className="ml-2 text-slate-500">Carregando...</span>
                        </div>
                      ) : historicoEscalas.length === 0 ? (
                        <div className="text-center py-8 border rounded-lg bg-slate-50">
                          <Clock className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                          <p className="text-slate-500">Nenhum histórico de escala encontrado</p>
                          <p className="text-sm text-slate-400 mt-1">
                            O histórico será registrado ao vincular escalas na gestão de escalas cíclicas.
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {historicoEscalas.map((historico, index) => (
                            <div 
                              key={historico.id} 
                              className={`p-4 border rounded-lg ${!historico.data_fim ? 'bg-green-50 border-green-200' : 'bg-white'}`}
                              data-testid={`historico-escala-${index}`}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <h4 className="font-semibold text-slate-800">
                                      {historico.escala_nome || 'Escala não identificada'}
                                    </h4>
                                    {!historico.data_fim && (
                                      <Badge variant="success" className="bg-green-100 text-green-700">
                                        Atual
                                      </Badge>
                                    )}
                                  </div>
                                  
                                  <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                                    <div>
                                      <span className="text-slate-500">Início:</span>
                                      <p className="font-medium">{formatarData(historico.data_inicio)}</p>
                                    </div>
                                    <div>
                                      <span className="text-slate-500">Fim:</span>
                                      <p className="font-medium">
                                        {historico.data_fim ? formatarData(historico.data_fim) : '-'}
                                      </p>
                                    </div>
                                    <div>
                                      <span className="text-slate-500">1ª Folga:</span>
                                      <p className="font-medium">{formatarData(historico.data_primeira_folga)}</p>
                                    </div>
                                    <div>
                                      <span className="text-slate-500">Dia Ciclo Início:</span>
                                      <p className="font-medium">Dia {historico.dia_inicio_ciclo || '-'}</p>
                                    </div>
                                  </div>

                                  {historico.escala_tamanho_ciclo && (
                                    <div className="mt-2 text-xs text-slate-500">
                                      Ciclo de {historico.escala_tamanho_ciclo} dias
                                      {historico.escala_formato && ` • Formato: ${historico.escala_formato}`}
                                    </div>
                                  )}

                                  {historico.observacao && (
                                    <p className="mt-2 text-sm text-slate-600 italic">
                                      "{historico.observacao}"
                                    </p>
                                  )}
                                </div>
                                
                                {/* Botões de ação */}
                                <div className="flex flex-col gap-1 ml-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleEditHistorico(historico)}
                                    title="Editar registro"
                                    data-testid={`btn-edit-historico-${index}`}
                                  >
                                    <Edit className="w-4 h-4 text-slate-500" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeleteHistorico(historico.id)}
                                    title="Excluir registro"
                                    data-testid={`btn-delete-historico-${index}`}
                                  >
                                    <Trash2 className="w-4 h-4 text-red-400" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      <div className="flex justify-end pt-4 border-t">
                        <Button variant="outline" onClick={() => {
                          setDialogOpen(false);
                          setEmpregadoSelecionado(null);
                          resetForm();
                        }}>
                          Fechar
                        </Button>
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>
              ) : (
                /* Formulário de cadastro (sem tabs) */
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="matricula">Matrícula *</Label>
                      <Input
                        id="matricula"
                        value={formData.matricula}
                        onChange={(e) => setFormData({...formData, matricula: e.target.value})}
                        required
                        data-testid="input-matricula"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="nome">Nome Completo *</Label>
                      <Input
                        id="nome"
                        value={formData.nome}
                        onChange={(e) => setFormData({...formData, nome: e.target.value})}
                        required
                        data-testid="input-nome"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cpf">CPF *</Label>
                      <Input
                        id="cpf"
                        value={formData.cpf}
                        onChange={(e) => setFormData({...formData, cpf: e.target.value})}
                        placeholder="000.000.000-00"
                        required
                        data-testid="input-cpf"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="data_admissao">Data Admissão *</Label>
                      <Input
                        id="data_admissao"
                        type="date"
                        value={formData.data_admissao}
                        onChange={(e) => setFormData({...formData, data_admissao: e.target.value})}
                        required
                        data-testid="input-data-admissao"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cargo">Cargo *</Label>
                      <Input
                        id="cargo"
                        value={formData.cargo}
                        onChange={(e) => setFormData({...formData, cargo: e.target.value})}
                        required
                        data-testid="input-cargo"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="setor">Setor *</Label>
                      <Input
                        id="setor"
                        value={formData.setor}
                        onChange={(e) => setFormData({...formData, setor: e.target.value})}
                        required
                        data-testid="input-setor"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="sindicato">Sindicato/Categoria</Label>
                      <Select
                        value={formData.sindicato_id || "none"}
                        onValueChange={(value) => setFormData({...formData, sindicato_id: value === "none" ? "" : value})}
                      >
                        <SelectTrigger data-testid="select-sindicato">
                          <SelectValue placeholder="Selecione (opcional)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Nenhum (usa regras gerais)</SelectItem>
                          {sindicatos.map(s => (
                            <SelectItem key={s.id} value={s.id}>
                              {s.nome} {s.sigla ? `(${s.sigla})` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">E-mail</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        data-testid="input-email"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="telefone">Telefone</Label>
                      <Input
                        id="telefone"
                        value={formData.telefone}
                        onChange={(e) => setFormData({...formData, telefone: e.target.value})}
                        data-testid="input-telefone"
                      />
                    </div>
                    <div className="space-y-2 flex items-end">
                      <div className="flex items-center space-x-2 bg-blue-50 p-3 rounded-lg w-full">
                        <input
                          type="checkbox"
                          id="opta_banco_horas"
                          checked={formData.opta_banco_horas || false}
                          onChange={(e) => setFormData({...formData, opta_banco_horas: e.target.checked})}
                          className="h-4 w-4 rounded border-blue-300 text-blue-600 focus:ring-blue-500"
                          data-testid="checkbox-opta-banco-horas"
                        />
                        <Label htmlFor="opta_banco_horas" className="cursor-pointer font-medium text-blue-800">
                          Opta por Banco de Horas
                        </Label>
                      </div>
                    </div>
                    <div className="col-span-2 space-y-2">
                      <Label htmlFor="escala">Escala</Label>
                      <Select
                        value={formData.escala_id}
                        onValueChange={(value) => setFormData({...formData, escala_id: value})}
                      >
                        <SelectTrigger data-testid="select-escala">
                          <SelectValue placeholder="Selecione uma escala" />
                        </SelectTrigger>
                        <SelectContent>
                          {escalas.map(escala => (
                            <SelectItem key={escala.id} value={escala.id}>
                              {escala.nome} ({escala.tipo})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2 space-y-2">
                      <Label htmlFor="lider">Líder Direto</Label>
                      <Select
                        value={formData.lider_id || "none"}
                        onValueChange={(value) => setFormData({...formData, lider_id: value === "none" ? "" : value})}
                      >
                        <SelectTrigger data-testid="select-lider">
                          <SelectValue placeholder="Selecione o líder direto" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Nenhum</SelectItem>
                          {lideres.map(lider => (
                            <SelectItem key={lider.id} value={lider.empregado_id || lider.usuario_id}>
                              {lider.empregado_nome || lider.usuario_nome} - {lider.empregado_setor || 'Sem setor'}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        O líder será responsável por aprovar justificativas deste empregado
                      </p>
                    </div>
                    
                    {/* Checkbox Não Controla Ponto */}
                    <div className="col-span-2 flex items-center space-x-2 bg-slate-50 p-3 rounded-lg">
                      <input
                        type="checkbox"
                        id="nao_controla_ponto"
                        checked={formData.nao_controla_ponto || false}
                        onChange={(e) => setFormData({...formData, nao_controla_ponto: e.target.checked})}
                        className="h-4 w-4 rounded border-slate-300 text-accent focus:ring-accent"
                        data-testid="checkbox-nao-controla-ponto"
                      />
                      <div>
                        <Label htmlFor="nao_controla_ponto" className="cursor-pointer font-medium">
                          Não controla ponto
                        </Label>
                        <p className="text-xs text-slate-500">
                          Marque para sócios, diretores ou cargos de confiança que não precisam registrar ponto. Não serão geradas inconsistências.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => {
                      setDialogOpen(false);
                      setEmpregadoSelecionado(null);
                      resetForm();
                    }}>
                      Cancelar
                    </Button>
                    <Button type="submit" className="bg-accent hover:bg-accent/90" data-testid="submit-empregado-btn">
                      Cadastrar
                    </Button>
                  </div>
                </form>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="border-slate-100">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Buscar empregado..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="search-empregado"
              />
            </div>
            <div className="text-sm text-muted-foreground">
              {filteredEmpregados.length} de {empregados.length} empregado(s)
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredEmpregados.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">Nenhum empregado encontrado</p>
              <p className="text-sm text-muted-foreground mt-1">
                Cadastre manualmente ou importe via planilha
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableTableHead column="matricula" label="Matrícula" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                    <SortableTableHead column="nome" label="Nome" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                    <SortableTableHead column="cpf" label="CPF" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                    <SortableTableHead column="cargo" label="Cargo" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                    <SortableTableHead column="setor" label="Setor" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                    <TableHead>Líder</TableHead>
                    <SortableTableHead column="ativo" label="Status" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                    <TableHead className="text-center">App Ponto</TableHead>
                    <TableHead className="text-center">Senha</TableHead>
                    <TableHead className="w-16 text-right pr-4"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEmpregados.map((empregado) => (
                    <TableRow key={empregado.id} data-testid={`empregado-row-${empregado.id}`}>
                      <TableCell className="font-mono text-sm">{empregado.matricula}</TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{empregado.nome}</p>
                          <p className="text-sm text-muted-foreground">{empregado.email || 'Sem email'}</p>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-sm">{empregado.cpf || '-'}</TableCell>
                      <TableCell>{empregado.cargo}</TableCell>
                      <TableCell>{empregado.setor}</TableCell>
                      <TableCell>
                        {empregado.lider_id ? (
                          <div className="flex items-center gap-1">
                            <span className="text-sm">
                              {(() => {
                                const lider = lideres.find(l => (l.empregado_id || l.usuario_id) === empregado.lider_id);
                                if (lider) return lider.empregado_nome || lider.usuario_nome;
                                // Se não encontrou nos líderes cadastrados, buscar nos empregados
                                const empLider = empregados.find(e => e.id === empregado.lider_id);
                                if (empLider) return empLider.nome;
                                return '-';
                              })()}
                            </span>
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={empregado.ativo ? 'default' : 'secondary'} className={empregado.ativo ? 'bg-accent' : ''}>
                          {empregado.ativo ? 'Ativo' : 'Inativo'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleAppPonto(empregado)}
                          className={empregado.app_ponto_autorizado 
                            ? 'text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50' 
                            : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50'
                          }
                          title={empregado.app_ponto_autorizado 
                            ? 'Autorizado - Clique para revogar' 
                            : 'Não autorizado - Clique para autorizar'
                          }
                          data-testid={`toggle-app-${empregado.id}`}
                        >
                          {empregado.app_ponto_autorizado ? (
                            <ShieldCheck className="w-5 h-5" />
                          ) : (
                            <ShieldX className="w-5 h-5" />
                          )}
                        </Button>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          {empregado.forcar_troca_senha ? (
                            <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50 text-xs">
                              <RefreshCw className="w-3 h-3 mr-1" />
                              Trocar
                            </Badge>
                          ) : empregado.senha_padrao !== false ? (
                            <Badge variant="outline" className="text-slate-500 border-slate-300 text-xs">
                              <Key className="w-3 h-3 mr-1" />
                              Padrão
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-emerald-600 border-emerald-300 bg-emerald-50 text-xs">
                              <KeyRound className="w-3 h-3 mr-1" />
                              OK
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right pr-2">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8"
                              data-testid={`empregado-actions-${empregado.id}`}
                            >
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuItem onClick={() => handleEdit(empregado)}>
                              <Edit className="w-4 h-4 mr-2" />
                              Editar
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => handleToggleAppPonto(empregado)}
                              className={empregado.app_ponto_autorizado ? 'text-amber-600' : 'text-emerald-600'}
                            >
                              {empregado.app_ponto_autorizado ? (
                                <>
                                  <ShieldX className="w-4 h-4 mr-2" />
                                  Revogar acesso ao App
                                </>
                              ) : (
                                <>
                                  <ShieldCheck className="w-4 h-4 mr-2" />
                                  Autorizar App Ponto
                                </>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleResetarSenha(empregado, false)}>
                              <Key className="w-4 h-4 mr-2" />
                              Resetar senha (padrão CPF)
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleResetarSenha(empregado, true)}>
                              <KeyRound className="w-4 h-4 mr-2" />
                              Gerar senha temporária
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleForcarTrocaSenha(empregado)} className="text-amber-600">
                              <RefreshCw className="w-4 h-4 mr-2" />
                              Forçar troca de senha
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleDelete(empregado.id)} className="text-destructive">
                              <Trash2 className="w-4 h-4 mr-2" />
                              Desativar
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => abrirDialogExclusao(empregado)} 
                              className="text-red-700 focus:text-red-700 focus:bg-red-50"
                            >
                              <UserX className="w-4 h-4 mr-2" />
                              Excluir Permanentemente
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog para exibir senha temporária */}
      <Dialog open={senhaDialogOpen} onOpenChange={setSenhaDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Senha Temporária Gerada</DialogTitle>
            <DialogDescription>
              Informe esta senha ao funcionário. Ele deverá trocá-la no próximo login.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-500 mb-1">Funcionário:</p>
              <p className="font-medium">{empregadoSelecionado?.nome}</p>
            </div>
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-700 mb-2">Nova senha temporária:</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-lg font-mono bg-white px-3 py-2 rounded border">
                  {senhaTemporaria}
                </code>
                <Button variant="outline" size="icon" onClick={copiarSenha}>
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <p className="text-xs text-slate-500">
              ⚠️ Esta senha será exibida apenas uma vez. Certifique-se de informá-la ao funcionário.
            </p>
          </div>
          <DialogFooter>
            <Button onClick={() => setSenhaDialogOpen(false)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de confirmação de exclusão permanente */}
      <Dialog open={excluirDialogOpen} onOpenChange={setExcluirDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-700 flex items-center gap-2">
              <UserX className="w-5 h-5" />
              Excluir Permanentemente
            </DialogTitle>
            <DialogDescription>
              Esta ação é <strong className="text-red-600">IRREVERSÍVEL</strong>. Todos os dados serão apagados.
            </DialogDescription>
          </DialogHeader>
          {empregadoExcluir && (
            <div className="space-y-4">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700 mb-2">Serão excluídos permanentemente:</p>
                <ul className="text-sm text-red-600 space-y-1 ml-4 list-disc">
                  <li>Cadastro de <strong>{empregadoExcluir.nome}</strong></li>
                  <li>Todas as marcações de ponto</li>
                  <li>Todas as justificativas</li>
                  <li>Dados biométricos</li>
                  <li>Notificações e solicitações</li>
                  <li>Vínculos de escala</li>
                </ul>
              </div>
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs text-amber-700">
                  <strong>Atenção:</strong> Se precisa apenas impedir acesso, use a opção Desativar ao invés de excluir.
                </p>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button 
              variant="outline" 
              onClick={() => {
                setExcluirDialogOpen(false);
                setEmpregadoExcluir(null);
              }}
              disabled={excluindo}
            >
              Cancelar
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleExcluirCompleto}
              disabled={excluindo}
              className="bg-red-700 hover:bg-red-800"
            >
              {excluindo ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Excluindo...
                </>
              ) : (
                <>
                  <UserX className="w-4 h-4 mr-2" />
                  Confirmar Exclusão
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Afastamento */}
      <Dialog open={afastamentoDialogOpen} onOpenChange={setAfastamentoDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">
              {afastamentoEdit ? 'Editar Afastamento' : 'Novo Período de Afastamento'}
            </DialogTitle>
            <DialogDescription>
              {afastamentoEdit ? 'Atualize os dados do período' : 'Cadastre um novo período de afastamento'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="tipo_afastamento">Tipo *</Label>
              <Select
                value={afastamentoForm.tipo}
                onValueChange={(value) => setAfastamentoForm({...afastamentoForm, tipo: value, tipo_outros: value !== 'outros' ? '' : afastamentoForm.tipo_outros})}
              >
                <SelectTrigger data-testid="select-tipo-afastamento">
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS_AFASTAMENTO.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {afastamentoForm.tipo === 'outros' && (
              <div className="space-y-2">
                <Label htmlFor="tipo_outros">Especifique *</Label>
                <Input
                  id="tipo_outros"
                  value={afastamentoForm.tipo_outros}
                  onChange={(e) => setAfastamentoForm({...afastamentoForm, tipo_outros: e.target.value})}
                  placeholder="Descreva o tipo de afastamento"
                  data-testid="input-tipo-outros"
                />
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="data_inicio_af">Data Início *</Label>
                <Input
                  id="data_inicio_af"
                  placeholder="DD/MM/AAAA"
                  value={afastamentoForm.data_inicio}
                  onChange={(e) => {
                    let value = e.target.value.replace(/\D/g, '');
                    if (value.length >= 2) value = value.slice(0, 2) + '/' + value.slice(2);
                    if (value.length >= 5) value = value.slice(0, 5) + '/' + value.slice(5, 9);
                    setAfastamentoForm({...afastamentoForm, data_inicio: value});
                  }}
                  maxLength={10}
                  data-testid="input-data-inicio-afastamento"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="data_fim_af">Data Fim *</Label>
                <Input
                  id="data_fim_af"
                  placeholder="DD/MM/AAAA"
                  value={afastamentoForm.data_fim}
                  onChange={(e) => {
                    let value = e.target.value.replace(/\D/g, '');
                    if (value.length >= 2) value = value.slice(0, 2) + '/' + value.slice(2);
                    if (value.length >= 5) value = value.slice(0, 5) + '/' + value.slice(5, 9);
                    setAfastamentoForm({...afastamentoForm, data_fim: value});
                  }}
                  maxLength={10}
                  data-testid="input-data-fim-afastamento"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="descricao_af">Descrição/Justificativa</Label>
              <Textarea
                id="descricao_af"
                value={afastamentoForm.descricao}
                onChange={(e) => setAfastamentoForm({...afastamentoForm, descricao: e.target.value})}
                placeholder="Observações sobre o afastamento (opcional)"
                rows={3}
                data-testid="textarea-descricao-afastamento"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAfastamentoDialogOpen(false)} disabled={salvandoAfastamento}>
              Cancelar
            </Button>
            <Button onClick={handleSalvarAfastamento} disabled={salvandoAfastamento} data-testid="btn-salvar-afastamento">
              {salvandoAfastamento ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Salvando...
                </>
              ) : (
                afastamentoEdit ? 'Atualizar' : 'Cadastrar'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo de Edição do Histórico de Escalas */}
      <Dialog open={editHistoricoDialog} onOpenChange={setEditHistoricoDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Editar Histórico de Escala</DialogTitle>
            <DialogDescription>
              Ajuste as datas e informações do registro de histórico
            </DialogDescription>
          </DialogHeader>
          {historicoEdit && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm font-medium text-slate-700">
                  {historicoEdit.escala_nome || 'Escala não identificada'}
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Data de Início</Label>
                  <Input
                    type="date"
                    value={historicoEdit.data_inicio || ''}
                    onChange={(e) => setHistoricoEdit({...historicoEdit, data_inicio: e.target.value})}
                    data-testid="input-hist-data-inicio"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Data de Fim</Label>
                  <Input
                    type="date"
                    value={historicoEdit.data_fim || ''}
                    onChange={(e) => setHistoricoEdit({...historicoEdit, data_fim: e.target.value})}
                    data-testid="input-hist-data-fim"
                  />
                  <p className="text-xs text-slate-500">Deixe vazio se for a escala atual</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Data da 1ª Folga</Label>
                  <Input
                    type="date"
                    value={historicoEdit.data_primeira_folga || ''}
                    onChange={(e) => setHistoricoEdit({...historicoEdit, data_primeira_folga: e.target.value})}
                    data-testid="input-hist-primeira-folga"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Dia Início no Ciclo</Label>
                  <Input
                    type="number"
                    min="1"
                    max="31"
                    value={historicoEdit.dia_inicio_ciclo || ''}
                    onChange={(e) => setHistoricoEdit({...historicoEdit, dia_inicio_ciclo: e.target.value})}
                    data-testid="input-hist-dia-ciclo"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Observação</Label>
                <Input
                  type="text"
                  value={historicoEdit.observacao || ''}
                  onChange={(e) => setHistoricoEdit({...historicoEdit, observacao: e.target.value})}
                  placeholder="Ex: Alteração de setor, mudança de turno..."
                  data-testid="input-hist-obs"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditHistoricoDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveHistorico} disabled={salvandoHistorico}>
              {salvandoHistorico ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Salvando...
                </>
              ) : (
                'Salvar'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmpregadosPage;
